import {useEffect, useState} from 'react';
import {importWithRetry} from '@chekin/core';

type LottieLayer = {
  nm: string;
  shapes: Shape[];
};
type Shape = {
  it: It[];
};

type It = {
  ty: string;
  c: {k: number[]};
};

function hexToRGBA(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const a = hex.length > 7 ? parseInt(hex.slice(7, 9), 16) / 255 : 1;
  return [r, g, b, a];
}

function resolveCssHexVariable(variableName: string) {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();

  return /^#([\da-f]{6}|[\da-f]{8})$/i.test(value) ? value : null;
}

const useCustomLotties = () => {
  const [checkinCompletedAnimation, setCheckinCompletedAnimation] = useState<
    object | null
  >(null);
  const [successAnimationIcon, setSuccessAnimationIcon] = useState<object | null>(null);

  useEffect(function lazyPreloadSuccessLottie() {
    const primaryColor = resolveCssHexVariable('--primary');

    importWithRetry(() => import('../assets/lotties/blue-checked.json'))
      .then(data => {
        const newSuccessIcon = JSON.parse(JSON.stringify(data?.default));
        newSuccessIcon.layers.forEach((layer: LottieLayer) => {
          if (layer.nm !== 'Tick') {
            layer.shapes.forEach((shape: Shape) => {
              shape.it?.forEach((it: It) => {
                if (
                  primaryColor &&
                  (it.ty === 'fl' || it.ty === 'st') &&
                  JSON.stringify(it.c.k) === JSON.stringify([0.1294, 0.2824, 1])
                ) {
                  it.c.k = hexToRGBA(primaryColor);
                }
              });
            });
          }
        });
        setSuccessAnimationIcon(newSuccessIcon);
      })
      .catch(error => {
        console.error('Failed to load success animation', error);
      });
  }, []);

  useEffect(function lazyPreloadCheckinCompleteLottie() {
    const primaryColor = resolveCssHexVariable('--primary');

    importWithRetry(() => import('../assets/lotties/checkin-online-complete.json'))
      .then(data => {
        const newData = JSON.parse(JSON.stringify(data.default));

        newData.layers.forEach((layer: LottieLayer) => {
          layer.shapes.forEach((shape: Shape) => {
            shape.it?.forEach((it: It) => {
              if (
                primaryColor &&
                (it.ty === 'fl' || it.ty === 'st') &&
                JSON.stringify(it.c.k) === JSON.stringify([0.1294, 0.2824, 1])
              ) {
                it.c.k = hexToRGBA(primaryColor);
              }
            });
          });
        });
        setCheckinCompletedAnimation(newData);
      })
      .catch(error => {
        console.error('Failed to load check-in complete animation', error);
      });
  }, []);

  return {
    successAnimationIcon,
    checkinCompletedAnimation,
  };
};

export {useCustomLotties};
